<script setup lang="ts">
import { computed } from 'vue'

const props = withDefaults(
  defineProps<{
    cardId: string
    transition?:
      | 'none'
      | 'fade'
      | 'flip'
      | 'slide-left'
      | 'slide-right'
      | 'swipe-up'
      | 'swipe-down'
      | 'reveal-under'
      | 'scale'
  }>(),
  { transition: 'none' },
)

const name = computed(() => {
  if (props.transition === 'none') return ''
  return `dp-${props.transition}`
})

const mode = computed(() => {
  // For “reveal under”, mount the new card first, then animate the old card away.
  if (props.transition === 'reveal-under') return 'in-out'
  return 'out-in'
})
</script>

<template>
  <div class="stage">
    <Transition :name="name" :mode="mode">
      <div :key="props.cardId" class="stage__item" :data-pfc-stage-card-id="props.cardId">
        <slot />
      </div>
    </Transition>
  </div>
</template>

<style scoped>
.stage {
  position: relative;
  display: flex;
  justify-content: center;
  perspective: 1000px;
}

.stage__item {
  width: fit-content;
}

/* Fade */
.dp-fade-enter-active,
.dp-fade-leave-active {
  transition: opacity 220ms ease;
}

.dp-fade-enter-from,
.dp-fade-leave-to {
  opacity: 0;
}

/* Slide left */
.dp-slide-left-enter-active,
.dp-slide-left-leave-active {
  transition:
    transform 260ms cubic-bezier(0.22, 1, 0.36, 1),
    opacity 260ms ease;
}

.dp-slide-left-enter-from {
  transform: translateX(18%);
  opacity: 0;
}

.dp-slide-left-leave-to {
  transform: translateX(-18%);
  opacity: 0;
}

/* Slide right */
.dp-slide-right-enter-active,
.dp-slide-right-leave-active {
  transition:
    transform 260ms cubic-bezier(0.22, 1, 0.36, 1),
    opacity 260ms ease;
}

.dp-slide-right-enter-from {
  transform: translateX(-18%);
  opacity: 0;
}

.dp-slide-right-leave-to {
  transform: translateX(18%);
  opacity: 0;
}

/* Swipe up */
.dp-swipe-up-enter-active,
.dp-swipe-up-leave-active {
  transition:
    transform 250ms cubic-bezier(0.22, 1, 0.36, 1),
    opacity 250ms ease;
}

.dp-swipe-up-enter-from {
  transform: translateY(18%);
  opacity: 0;
}

.dp-swipe-up-leave-to {
  transform: translateY(-18%);
  opacity: 0;
}

/* Swipe down */
.dp-swipe-down-enter-active,
.dp-swipe-down-leave-active {
  transition:
    transform 250ms cubic-bezier(0.22, 1, 0.36, 1),
    opacity 250ms ease;
}

.dp-swipe-down-enter-from {
  transform: translateY(-18%);
  opacity: 0;
}

.dp-swipe-down-leave-to {
  transform: translateY(18%);
  opacity: 0;
}

/* Flip (card-to-card) */
.dp-flip-enter-active,
.dp-flip-leave-active {
  transition:
    transform 320ms cubic-bezier(0.22, 1, 0.36, 1),
    opacity 320ms ease;
  transform-style: preserve-3d;
  backface-visibility: hidden;
}

.dp-flip-enter-from {
  transform: rotateY(-90deg);
  opacity: 0;
}

.dp-flip-leave-to {
  transform: rotateY(90deg);
  opacity: 0;
}

/* Reveal under (old card swipes away, new card already behind) */
.dp-reveal-under-enter-active {
  transition: none;
}

.dp-reveal-under-leave-active {
  position: absolute;
  left: 50%;
  top: 0;
  width: fit-content;
  z-index: 2;
  transition:
    transform 260ms cubic-bezier(0.22, 1, 0.36, 1),
    opacity 260ms ease;
}

.dp-reveal-under-leave-from {
  transform: translateX(-50%) translate(0, 0);
  opacity: 1;
}

.dp-reveal-under-leave-to {
  transform: translateX(-50%) translate(-12%, 18%) rotate(-2deg);
  opacity: 0;
}

/* Scale */
.dp-scale-enter-active,
.dp-scale-leave-active {
  transition:
    transform 220ms cubic-bezier(0.22, 1, 0.36, 1),
    opacity 220ms ease;
}

.dp-scale-enter-from {
  transform: scale(0.97);
  opacity: 0;
}

.dp-scale-leave-to {
  transform: scale(1.03);
  opacity: 0;
}
</style>
